import { IconButton, NumberField, TextField } from '../ui/controls.js';
import { isNum, isObj, midiToNote, noteToMidi } from './param-values.js';

export type NoteRow = Record<string, unknown>;

/** A pitch cell's text: note names stay, numbers show as numbers, `{ hz }` as "440hz". */
export function pitchText(p: unknown): string {
  if (isNum(p)) return String(p);
  if (typeof p === 'string') return p;
  if (isObj(p) && isNum(p.hz)) return `${p.hz}hz`;
  if (isObj(p) && isNum(p.deg)) return `deg ${p.deg}${isNum(p.oct) ? ` oct ${p.oct}` : ''}`;
  return '';
}

/** Parses a pitch cell: "61" → 61, "440hz" → { hz: 440 }, "deg 5 oct 4" → { deg, oct }, else the text. */
export function parsePitch(text: string): unknown {
  const t = text.trim();
  if (t === '') return undefined;
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  const hz = /^(\d+(\.\d+)?)\s*hz$/i.exec(t);
  if (hz) return { hz: Number(hz[1]) };
  const deg = /^deg\s*(-?\d+)(?:\s*oct\s*(-?\d+))?$/i.exec(t);
  if (deg) return deg[2] !== undefined ? { deg: Number(deg[1]), oct: Number(deg[2]) } : { deg: Number(deg[1]) };
  return t;
}

/**
 * Editable note list (`{ t, d, p | chord, v, lyric? }`, times in beats for
 * patterns / seconds for inline notes). Unknown keys are preserved.
 */
export function NotesTable({
  notes,
  onChange,
  lyric = false,
  timeUnit = 'beats',
}: {
  notes: unknown[];
  onChange: (notes: NoteRow[]) => void;
  lyric?: boolean;
  timeUnit?: 'beats' | 's';
}) {
  const rows: NoteRow[] = notes.map((n) => (isObj(n) ? n : {}));
  const set = (i: number, key: string, v: unknown) => {
    const next = rows.map((r) => ({ ...r }));
    const row = next[i]!;
    if (v === undefined) delete row[key];
    else row[key] = v;
    onChange(next);
  };
  const add = () => {
    const last = rows[rows.length - 1];
    const t = last && isNum(last.t) ? last.t + (isNum(last.d) ? last.d : 1) : 0;
    const p = last?.p ?? 'C4';
    onChange([...rows, { t, d: isNum(last?.d) ? last.d : 1, p, v: isNum(last?.v) ? last.v : 0.8, ...(lyric ? { lyric: '' } : {}) }]);
  };
  const transpose = (semi: number) =>
    onChange(
      rows.map((r) => {
        if (isNum(r.p)) return { ...r, p: r.p + semi };
        if (typeof r.p === 'string') {
          const m = noteToMidi(r.p);
          if (m !== null) return { ...r, p: midiToNote(m + semi) };
        }
        return r;
      }),
    );

  return (
    <div className="snd-notes">
      <div className={`snd-notes-head${lyric ? ' has-lyric' : ''}`}>
        <span title={`Start (${timeUnit})`}>t</span>
        <span title={`Duration (${timeUnit})`}>d</span>
        <span title="Pitch (MIDI, note name, 440hz, deg 5 oct 4) or chord">pitch / chord</span>
        <span title="Velocity 0..1">v</span>
        {lyric && <span title="Lyric syllable sung on this note">lyric</span>}
        <span />
      </div>
      {rows.map((r, i) => (
        <div key={i} className={`snd-notes-row${lyric ? ' has-lyric' : ''}`}>
          <NumberField value={isNum(r.t) ? r.t : undefined} step={0.25} min={0} onChange={(v) => set(i, 't', v)} />
          <NumberField value={isNum(r.d) ? r.d : undefined} step={0.25} min={0} onChange={(v) => set(i, 'd', v)} />
          {typeof r.chord === 'string' ? (
            <TextField value={r.chord} mono placeholder="Am7" onChange={(t) => set(i, 'chord', t.trim() || undefined)} />
          ) : (
            <TextField value={pitchText(r.p)} mono placeholder="C4" onChange={(t) => set(i, 'p', parsePitch(t))} />
          )}
          <NumberField value={isNum(r.v) ? r.v : undefined} step={0.05} min={0} max={1} onChange={(v) => set(i, 'v', v)} />
          {lyric && <TextField value={typeof r.lyric === 'string' ? r.lyric : ''} placeholder="la" onChange={(t) => set(i, 'lyric', t || undefined)} />}
          <IconButton icon="x" title="Remove note" size={12} onClick={() => onChange(rows.filter((_, j) => j !== i))} />
        </div>
      ))}
      <div className="snd-row">
        <IconButton icon="plus" title="Add note" size={13} onClick={add} />
        <IconButton icon="arrowUp" title="Transpose up a semitone" size={13} onClick={() => transpose(1)} />
        <IconButton icon="arrowDown" title="Transpose down a semitone" size={13} onClick={() => transpose(-1)} />
        <span className="snd-hint">{`${rows.length} notes · times in ${timeUnit}`}</span>
      </div>
    </div>
  );
}
